import React, { useEffect, useRef, useState } from "react";
import { Alert, Box, Button, CircularProgress, IconButton, Paper, Snackbar, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SaveIcon from "@mui/icons-material/Save";
import { addDoc, collection, doc, getDocs, limit, query, Timestamp, updateDoc } from "firebase/firestore";
import Navbar from "../Components/Navbar/navbar";
import HistoricoCadastro from "../Components/HistoricoCadastro/HistoricoCadastro";
import { auth, db } from "../Config/firebase";
import { AMBIENTE_PRODUCAO } from "../Config/branding";
import { aplicarTenantAtual, consultaTenantAtual } from "../Utils/tenant";
import { CAMPOS_RESIDUO, COLLECTION_CONTROLE_RESIDUOS, novoResiduo, RESIDUOS_INICIAIS } from "./controleResiduosData";

const OBJETIVO = "Estabelecer os critérios e responsabilidades para o gerenciamento dos resíduos gerados nas atividades operacionais, administrativas e de manutenção do Laboratório Eternita, abrangendo identificação, segregação, armazenamento temporário e destinação final adequada.";
const cabecalho = { bgcolor: "#0b3d47", color: "white", border: "1px solid #94a3b8", fontWeight: 800, textAlign: "center" };
const celula = { border: "1px solid #1f2937", color: "#003bca", textAlign: "center", whiteSpace: "normal", overflowWrap: "break-word", lineHeight: 1.25, py: 0.65, cursor: "text", verticalAlign: "middle" };
const larguras = { tipoResiduo: 230, classeResiduo: 260, mesReferencia: 150, origemSetor: 220, quantidadeTotal: 90, unidade: 100, destinacaoFinal: 320 };
const LARGURA_TABELA = 1480;

function usuarioAtual() {
  return {
    uid: auth.currentUser?.uid || "",
    nome: auth.currentUser?.displayName || auth.currentUser?.email || "Não informado",
    email: auth.currentUser?.email || "",
  };
}

function montarCadastro(id, dados) {
  return {
    id,
    revisao: dados.revisao || "",
    atualizadoEmDocumento: dados.atualizadoEmDocumento || "",
    criadoEm: dados.criadoEm || null,
    criadoPor: dados.criadoPor || null,
    criadoPorNome: dados.criadoPorNome || "",
    criadoPorEmail: dados.criadoPorEmail || "",
    atualizadoEm: dados.atualizadoEm || null,
    atualizadoPor: dados.atualizadoPor || null,
    atualizadoPorNome: dados.atualizadoPorNome || "",
    atualizadoPorEmail: dados.atualizadoPorEmail || "",
    historico: Array.isArray(dados.historico) ? dados.historico : [],
    residuos: (dados.residuos || []).map((item, indice) => ({ ...novoResiduo(indice + 1), ...item, item: indice + 1 })),
  };
}

export default function CadastroControleResiduos() {
  const criandoRef = useRef(false);
  const tabelaRef = useRef(null);
  const barraInferiorRef = useRef(null);
  const [cadastro, setCadastro] = useState(null);
  const [semCadastro, setSemCadastro] = useState(false);
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [alterado, setAlterado] = useState(false);
  const [notificacao, setNotificacao] = useState({ aberta: false, mensagem: "", tipo: "success" });

  useEffect(() => { (async () => {
    try {
      const snapshot = await getDocs(query(consultaTenantAtual(collection(db, COLLECTION_CONTROLE_RESIDUOS)), limit(1)));
      if (snapshot.empty) setSemCadastro(true);
      else setCadastro(montarCadastro(snapshot.docs[0].id, snapshot.docs[0].data()));
    } catch (erro) {
      console.error("Erro ao carregar controle de resíduos:", erro);
      setNotificacao({ aberta: true, mensagem: "Não foi possível carregar o controle de resíduos.", tipo: "error" });
    } finally { setCarregando(false); }
  })(); }, []);

  async function cadastrarDadosExistentes() {
    if (criandoRef.current) return;
    criandoRef.current = true;
    setSalvando(true);
    try {
      const agora = Timestamp.now();
      const usuario = usuarioAtual();
      const dados = aplicarTenantAtual({
        codigoDocumento: "RQ 10.2",
        revisao: AMBIENTE_PRODUCAO ? "01" : "",
        atualizadoEmDocumento: AMBIENTE_PRODUCAO ? "01/09/2025" : "",
        objetivo: OBJETIVO,
        residuos: RESIDUOS_INICIAIS.map((item) => ({ ...item })),
        criadoEm: agora,
        criadoPor: usuario.uid,
        criadoPorNome: usuario.nome,
        criadoPorEmail: usuario.email,
        atualizadoEm: agora,
        atualizadoPor: usuario.uid,
        atualizadoPorNome: usuario.nome,
        atualizadoPorEmail: usuario.email,
        historico: [{ acao: "CRIACAO", descricao: "Controle de resíduos cadastrado com os dados existentes.", usuario, data: agora }],
      });
      const criado = await addDoc(collection(db, COLLECTION_CONTROLE_RESIDUOS), dados);
      setCadastro(montarCadastro(criado.id, dados));
      setSemCadastro(false);
      setAlterado(false);
      setNotificacao({ aberta: true, mensagem: "Controle de resíduos cadastrado com sucesso.", tipo: "success" });
    } catch (erro) {
      console.error("Erro ao cadastrar controle de resíduos:", erro);
      setNotificacao({ aberta: true, mensagem: "Não foi possível cadastrar o controle de resíduos.", tipo: "error" });
    } finally {
      criandoRef.current = false;
      setSalvando(false);
    }
  }

  function atualizarCadastro(campo, valor) {
    setCadastro((atual) => {
      if (atual[campo] === valor) return atual;
      setAlterado(true);
      return { ...atual, [campo]: valor };
    });
  }
  function atualizarResiduo(indice, campo, valor) {
    setCadastro((atual) => {
      if (String(atual.residuos[indice]?.[campo] || "") === valor) return atual;
      setAlterado(true);
      return { ...atual, residuos: atual.residuos.map((item, posicao) => posicao === indice ? { ...item, [campo]: valor } : item) };
    });
  }
  function adicionarResiduo() {
    setCadastro((atual) => ({ ...atual, residuos: [...atual.residuos, novoResiduo(atual.residuos.length + 1)] }));
    setAlterado(true);
  }
  function removerResiduo(indice) {
    setCadastro((atual) => ({ ...atual, residuos: atual.residuos.filter((_, posicao) => posicao !== indice).map((item, posicao) => ({ ...item, item: posicao + 1 })) }));
    setAlterado(true);
  }

  async function salvar() {
    if (cadastro.residuos.some((item) => !String(item.tipoResiduo || "").trim())) {
      setNotificacao({ aberta: true, mensagem: "Informe o tipo de resíduo em todas as linhas.", tipo: "warning" });
      return;
    }
    setSalvando(true);
    try {
      const residuos = cadastro.residuos.map((item, indice) => ({ item: indice + 1, ...Object.fromEntries(CAMPOS_RESIDUO.map(([campo]) => [campo, String(item[campo] || "").trim()])) }));
      const agora = Timestamp.now();
      const usuario = usuarioAtual();
      const historicoAnterior = cadastro.historico?.length ? cadastro.historico : cadastro.criadoEm ? [{ acao: "CRIACAO", descricao: "Controle de resíduos cadastrado no sistema.", usuario: { uid: cadastro.criadoPor || "", nome: cadastro.criadoPorNome || "Não informado", email: cadastro.criadoPorEmail || "" }, data: cadastro.criadoEm }] : [];
      const historico = [...historicoAnterior, { acao: "ALTERACAO", descricao: "Alterações salvas no controle de resíduos.", usuario, data: agora }];
      await updateDoc(doc(db, COLLECTION_CONTROLE_RESIDUOS, cadastro.id), {
        revisao: cadastro.revisao.trim(), atualizadoEmDocumento: cadastro.atualizadoEmDocumento.trim(), residuos,
        atualizadoEm: agora, atualizadoPor: usuario.uid, atualizadoPorNome: usuario.nome, atualizadoPorEmail: usuario.email, historico,
      });
      setCadastro((atual) => ({ ...atual, residuos, atualizadoEm: agora, atualizadoPor: usuario.uid, atualizadoPorNome: usuario.nome, atualizadoPorEmail: usuario.email, historico }));
      setAlterado(false);
      setNotificacao({ aberta: true, mensagem: "Controle de resíduos salvo com sucesso.", tipo: "success" });
    } catch (erro) {
      console.error("Erro ao salvar controle de resíduos:", erro);
      setNotificacao({ aberta: true, mensagem: "Não foi possível salvar o controle de resíduos.", tipo: "error" });
    } finally { setSalvando(false); }
  }

  return <><Navbar /><Box sx={{ p: { xs: 1, md: 3 }, mt: 6, pb: 5 }}>
    {carregando && <Box sx={{ p: 4, textAlign: "center" }}><CircularProgress size={24} /></Box>}
    {!carregando && semCadastro && <Paper elevation={0} className="cabecalho-lista-cadastro" sx={{ p: 2 }}><Typography variant="h5" fontWeight={700}>RQ 10.2 — Volume e Controle de Resíduos</Typography><Typography color="text.secondary" sx={{ my: 2 }}>O cadastro é único por empresa. Cadastre os registros existentes para iniciar.</Typography><Button variant="contained" onClick={cadastrarDadosExistentes} disabled={salvando}>{salvando ? "Cadastrando..." : "Cadastrar dados existentes"}</Button></Paper>}
    {cadastro && <><Paper elevation={0} className="cabecalho-lista-cadastro" sx={{ p: 2, mb: 1.5, overflowX: "auto" }}><Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 1280, whiteSpace: "nowrap" }}><Typography variant="h5" fontWeight={700} sx={{ mr: "auto" }}>RQ 10.2 — Volume e Controle de Resíduos</Typography><TextField size="small" label="Revisão" value={cadastro.revisao} onChange={(event) => atualizarCadastro("revisao", event.target.value)} placeholder="Alterar número da revisão" sx={{ width: 190, flexShrink: 0 }} /><TextField size="small" label="Atualizado em" value={cadastro.atualizadoEmDocumento} onChange={(event) => atualizarCadastro("atualizadoEmDocumento", event.target.value)} placeholder="DD/MM/AAAA" sx={{ width: 180, flexShrink: 0 }} /><HistoricoCadastro colecao={COLLECTION_CONTROLE_RESIDUOS} documentoId={cadastro.id} registro={cadastro} iconeApenas={false} /><Button variant="outlined" startIcon={<AddIcon />} onClick={adicionarResiduo} sx={{ flexShrink: 0 }}>Adicionar resíduo</Button><Button variant="contained" startIcon={<SaveIcon />} onClick={salvar} disabled={salvando || !alterado} sx={{ flexShrink: 0 }}>{salvando ? "Salvando..." : "Salvar alterações"}</Button></Stack></Paper>
    <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5, bgcolor: "#d1f0d9", borderColor: "#6dbb7a" }}><Typography variant="body2"><strong>Objetivo:</strong> {OBJETIVO}</Typography></Paper>
    <Paper ref={tabelaRef} onScroll={(event) => { if (barraInferiorRef.current) barraInferiorRef.current.scrollLeft = event.currentTarget.scrollLeft; }} variant="outlined" sx={{ overflowX: "auto", width: "100%", scrollbarWidth: "none", "&::-webkit-scrollbar": { display: "none" } }}><Table size="small" sx={{ minWidth: LARGURA_TABELA, tableLayout: "fixed" }}><TableHead><TableRow><TableCell sx={{ ...cabecalho, bgcolor: "#1e1156", width: 55 }}>Item</TableCell>{CAMPOS_RESIDUO.map(([campo, titulo]) => <TableCell key={campo} sx={{ ...cabecalho, bgcolor: "#1e1156", width: larguras[campo] }}>{titulo}</TableCell>)}<TableCell sx={{ ...cabecalho, bgcolor: "#1e1156", width: 55 }} /></TableRow></TableHead><TableBody>{cadastro.residuos.map((item, indice) => <TableRow key={indice}><TableCell sx={{ ...celula, fontWeight: 700, width: 55 }}>{indice + 1}</TableCell>{CAMPOS_RESIDUO.map(([campo]) => <TableCell key={campo} contentEditable suppressContentEditableWarning sx={{ ...celula, width: larguras[campo] }} onInput={() => setAlterado(true)} onBlur={(event) => atualizarResiduo(indice, campo, event.currentTarget.innerText)}>{item[campo] || ""}</TableCell>)}<TableCell sx={{ ...celula, cursor: "default" }}><Tooltip title="Remover resíduo"><IconButton size="small" color="error" onClick={() => removerResiduo(indice)}><DeleteOutlineIcon fontSize="small" /></IconButton></Tooltip></TableCell></TableRow>)}</TableBody></Table></Paper>
    <Box ref={barraInferiorRef} onScroll={(event) => { if (tabelaRef.current) tabelaRef.current.scrollLeft = event.currentTarget.scrollLeft; }} sx={{ position: "fixed", bottom: 0, left: { xs: 8, md: 24 }, width: { xs: "calc(100vw - 16px)", md: "calc(100vw - 48px)" }, height: 20, overflowX: "auto", overflowY: "hidden", bgcolor: "background.paper", borderTop: "1px solid #94a3b8", zIndex: 1300 }}><Box sx={{ width: LARGURA_TABELA, height: 1 }} /></Box></>}
  </Box><Snackbar open={notificacao.aberta} autoHideDuration={5000} onClose={() => setNotificacao((atual) => ({ ...atual, aberta: false }))}><Alert severity={notificacao.tipo} onClose={() => setNotificacao((atual) => ({ ...atual, aberta: false }))}>{notificacao.mensagem}</Alert></Snackbar></>;
}
